import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import NumberInput from "../../../framework/NumberInput";
import SelectInput from "../../../framework/SelectInput";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { gstValues } from "../../../utils/constant";
import { formStateHandler, isEmpty } from "../../../utils/utils";
import { transformPayload } from "../ServiceItemConstants";
import classes from "./index.module.css";
import { saveServiceItem } from "../../../services/service-item-services";
import { searchHsn } from "../../../services/hsn-services";
const AddServiceItem = ({
  id,
  onSaveServiceItem = () => {},
  onEscape = () => {},
  serviceItemData = null,
}) => {
  const [formState, setFormState] = useState({});
  const hsnCodeRef = useRef("");
  const gstRef = useRef("");
  const serviceItemRemarkRef = useRef("");
  const serviceItemRef = useRef("");
  const saveServiceItemRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const [hsnOptions, setHsnOptions] = useState([]);
  const validateForm = useCallback(
    async (fieldName, data) => {
      if (fieldName === "productName" || !fieldName) {
        if (isEmpty(serviceItemRef.current.value)) {
          displayToast({
            type: "error",
            title: "Please enter a valid Service Item",
          });
          serviceItemRef.current?.focus();
          return false;
        }
      }
      if (fieldName === "mrp" || !fieldName) {
        if (isEmpty(serviceItemRef.current.value)) {
          displayToast({
            type: "error",
            title: "Please enter a valid MRP",
          });
          serviceItemRef.current?.focus();
          return false;
        }
      }
      if (fieldName === "hsnCode" || !fieldName) {
        if (!formState?.hsnCode) {
          displayToast({
            type: "error",
            title: "Please select a valid HSN Code",
          });
          hsnCodeRef.current?.focus();
          return false;
        }
      }
      if (fieldName === "gst" || !fieldName) {
        if (isEmpty(gstRef.current.value)) {
          displayToast({
            type: "error",
            title: "Please enter a valid GST",
          });
          gstRef.current?.focus();
          return false;
        }
      }

      return true;
    },
    [formState]
  );
  const updateServiceItemHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({ ...formState });
      return saveServiceItem(payload).then((r) => {
        if (!r) return;
        onSaveServiceItem(r);
        return displayToast({
          type: "success",
          title: "Service Item added successfully.",
        });
      });
    },
    [formState, onSaveServiceItem, validateForm]
  );
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveServiceItemRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    if (serviceItemData) {
      setFormState({
        ...serviceItemData,
        gst: gstValues.find((g) => g.id === serviceItemData.gst),
      });
    }
  }, [serviceItemData]);
  return (
    <div>
      <h4 className={classes["service-item-detail-label"]}>Service Item Details</h4>
      <Grid container spacing={2} sx={{ p: "25px 20px" }}>
        <Grid item xs={4}>
          <TextInput
            id="add_service_item"
            name="productName"
            label="Service Item"
            placeholder="Enter Service Item"
            ref={serviceItemRef}
            value={formState?.productName || ""}
            onFocus={() => setCurrentComponent("add_service_item")}
            onChange={(e) => {
              handleFormChange("productName", e.target.value);
            }}
            required
            autoFocus
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_service_item",
                down: "service_item_remarks",
                left: "service_item_remarks",
                right: "add_service_item_mrp",
              }),
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <NumberInput
            id="add_service_item_mrp"
            name="mrp"
            label="MRP ₹"
            decimalScale={2}
            value={formState?.mrp || ""}
            onFocus={() => setCurrentComponent("add_service_item_mrp")}
            onChange={(e) => {
              handleFormChange("mrp", e.target.value);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                left: "add_service_item",
                right: "add_hsn_gst",
                up: "add_service_item_mrp",
                down: "service_item_remarks",
              }),
            }}
            allowNegative={false}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <SelectInput
            id="add_hsn_gst"
            ref={gstRef}
            name="gst"
            label="GST"
            value={formState?.gst || null}
            onFocus={() => setCurrentComponent("add_hsn_gst")}
            onChange={(event, option) => {
              handleFormChange("gst", option);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => {
              return option.label;
            }}
            options={gstValues}
            markers={JSON.stringify({
              up: "add_hsn_gst",
              down: "service_item_remarks",
              left: "add_service_item_mrp",
              right: "add_hsn_code",
            })}
            required
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            id="add_hsn_code"
            name="hsnCode"
            value={formState?.hsnCode || ""}
            label="HSN Code"
            ref={hsnCodeRef}
            disableClearable={false}
            noOptionsText={"Search HSN code"}
            onFocus={() => setCurrentComponent("add_hsn_code")}
            onBlur={() => validateForm("add_hsn_code", formState)}
            options={hsnOptions}
            onInputChange={(e, newValue) => {
              if (!newValue) return handleFormChange("hsnCode", null);
              if (newValue.length >= 3) {
                searchHsn(`search=${newValue.trim()}`).then((resp) => {
                  const updatedResp = resp
                    .filter((prod) => prod?.hsnCodeId && prod?.hsnCode)
                    .map((prod) => ({
                      id: prod.hsnCodeId,
                      label: prod.hsnCode,
                    }));
                  setHsnOptions(updatedResp);
                });
              } else {
                setHsnOptions([]);
              }
            }}
            onChange={(event, option) => {
              handleFormChange("hsnCode", option?.label || null);
            }}
            getOptionLabel={(option) => {
              if (!option) return "";
              return option.label || option;
            }}
            isOptionEqualToValue={(option, value) => option === value}
            markers={JSON.stringify({
              up: "add_hsn_code",
              down: "service_item_remarks",
              left: "add_hsn_gst",
              right: "service_item_remarks",
            })}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            id="service_item_remarks"
            ref={serviceItemRemarkRef}
            name="remarks"
            label="Remarks"
            value={formState?.remarks || ""}
            onFocus={() => setCurrentComponent("service_item_remarks")}
            onChange={(e) => {
              handleFormChange("remarks", e.target.value);
            }}
            maxInputLength={2000}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_service_item",
                down: "service_item_remarks",
                left: "add_hsn_code",
                right: "add_service_item",
              }),
            }}
          />
        </Grid>
      </Grid>
      <div className={classes["add-service-item-save-button-wrapper"]}>
        <Button
          id="add_service_item_save_button"
          ref={saveServiceItemRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Service Item"
          color="violet"
          onClick={updateServiceItemHandler}
        />
      </div>
    </div>
  );
};

export default AddServiceItem;
